
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface AddReviewDialogProps {
  bookingId: string;
  providerName: string;
  providerId?: string;
  onReviewAdded?: () => void;
  brandColor?: string;
}

const AddReviewDialog = ({ bookingId, providerName, providerId, onReviewAdded, brandColor }: AddReviewDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Rating required",
        description: "Please select a star rating before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Submitting review for booking:', bookingId);
      
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get the booking details to get the provider_id
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('provider_id')
        .eq('id', bookingId)
        .single();

      if (bookingError) {
        console.error('Error fetching booking:', bookingError);
        throw bookingError;
      }

      console.log('Found booking:', booking);

      // Insert the review
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert({
          booking_id: bookingId,
          provider_id: booking.provider_id,
          user_id: user.id,
          rating,
          comment: comment.trim() || null,
        });

      if (reviewError) {
        console.error('Error creating review:', reviewError);
        throw reviewError;
      }

      console.log('Review created successfully');

      // Discord notification (non-fatal)
      const customerName = (user.user_metadata as any)?.full_name
        || `${(user.user_metadata as any)?.first_name || ''} ${(user.user_metadata as any)?.last_name || ''}`.trim()
        || user.email
        || 'Anonymous';
      try {
        await supabase.functions.invoke('notify-discord', {
          body: {
            event: 'new_review',
            data: {
              provider_name: providerName,
              customer_name: customerName,
              rating,
              comment: comment.trim() || null,
            },
          },
        });
      } catch (dErr) {
        console.error('Discord notify (review) failed:', dErr);
      }

      // Provider email notification (non-fatal)
      try {
        await supabase.functions.invoke('notify-provider-new-review', {
          body: {
            provider_id: booking.provider_id,
            rating,
            comment: comment.trim() || null,
            customer_name: customerName,
          },
        });
      } catch (eErr) {
        console.error('Provider review email failed:', eErr);
      }

      // Invalidate and refetch related queries to show updated data
      await queryClient.invalidateQueries({ queryKey: ['reviews', booking.provider_id] });
      await queryClient.invalidateQueries({ queryKey: ['provider'] });
      await queryClient.invalidateQueries({ queryKey: ['providers'] });
      
      // If we have the provider ID, also invalidate provider-specific queries
      if (providerId) {
        await queryClient.invalidateQueries({ queryKey: ['provider', providerId] });
      }

      toast({
        title: "Review submitted",
        description: "Thank you for your feedback!",
      });

      setIsOpen(false);
      setRating(0);
      setComment("");
      
      if (onReviewAdded) {
        onReviewAdded();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          style={brandColor ? { 
            borderColor: `${brandColor}40`,
            color: brandColor
          } : undefined}
        >
          Leave Review
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Leave a Review</DialogTitle>
          <DialogDescription>
            Share your experience with {providerName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star
                    className={`h-6 w-6 ${
                      star <= rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Comment (optional)</label>
            <Textarea
              placeholder="Tell others about your experience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            style={brandColor ? { backgroundColor: brandColor } : undefined}
          >
            {isSubmitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddReviewDialog;
